import React, { useEffect, useState } from 'react';
import { pnidApi, VersionInfo, VersionData } from '../api';
import { PnidWizardState } from '../types';
import {
  getEquipmentContextInstruments,
  getNodeContextInstruments,
} from '../utils/unifiedInstruments';

interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  docId: string | null;
  currentUser?: string;
  onRestore?: (state: PnidWizardState) => void;
  /** Preview a version without restoring to DB (temporary view) */
  onPreview?: (state: PnidWizardState, versionNumber: number) => void;
  /** Currently previewing version number (if any) */
  previewingVersion?: number | null;
  /** Exit preview mode without restoring */
  onExitPreview?: () => void;
}

const VersionHistoryModal: React.FC<VersionHistoryModalProps> = ({
  isOpen,
  onClose,
  docId,
  currentUser,
  onRestore,
  onPreview,
  previewingVersion,
  onExitPreview,
}) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [versions, setVersions] = useState<VersionInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<VersionData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      const timer = setTimeout(() => setVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Fetch versions when modal opens
  useEffect(() => {
    if (isOpen && docId) {
      setLoading(true);
      setError(null);
      setSelectedVersion(null);
      setConfirmRestore(null);
      pnidApi.getVersionHistory(docId)
        .then((result) => {
          setVersions(result.versions);
          // If we're previewing a version, auto-load its details
          if (previewingVersion) {
            const version = result.versions.find(v => v.version_number === previewingVersion);
            if (version) {
              handlePreviewVersion(previewingVersion);
            }
          }
        })
        .catch((err) => {
          console.error('Failed to load version history:', err);
          setError('Failed to load version history');
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen, docId, previewingVersion]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (confirmRestore !== null) {
          setConfirmRestore(null);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, confirmRestore]);

  const handlePreviewVersion = async (versionNumber: number) => {
    if (!docId) return;
    setPreviewLoading(true);
    try {
      const data = await pnidApi.getVersion(docId, versionNumber);
      setSelectedVersion(data);
    } catch (err) {
      console.error('Failed to load version:', err);
      setError('Failed to load version details');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleRestoreVersion = async (versionNumber: number) => {
    if (!docId) return;
    setRestoring(true);
    try {
      const state = await pnidApi.restoreVersion(docId, versionNumber, currentUser);
      onRestore?.(state);
      setConfirmRestore(null);
      onClose();
    } catch (err) {
      console.error('Failed to restore version:', err);
      setError('Failed to restore version');
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFullDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Get summary stats for a version's data
  const getVersionSummary = (data: PnidWizardState) => {
    return {
      equipments: data.equipments?.length || 0,
      nodes: data.nodes?.length || 0,
      equipmentInstruments: getEquipmentContextInstruments(data).length,
      nodeInstruments: getNodeContextInstruments(data).length,
    };
  };

  if (!mounted) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 200ms ease-out',
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'relative',
          backgroundColor: 'white',
          borderRadius: 12,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          width: '90%',
          maxWidth: 600,
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          transform: visible ? 'scale(1)' : 'scale(0.95)',
          opacity: visible ? 1 : 0,
          transition: 'transform 200ms ease-out, opacity 200ms ease-out',
        }}
      >
        {/* Preview Banner */}
        {previewingVersion && onExitPreview && (
          <div
            style={{
              padding: '10px 20px',
              backgroundColor: '#fef3c7',
              borderBottom: '1px solid #fcd34d',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16 }}>👁️</span>
              <span style={{ fontSize: 13, fontWeight: 500, color: '#92400e' }}>
                Viewing version {previewingVersion} (not saved)
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={onExitPreview}
                style={{
                  padding: '5px 12px',
                  fontSize: 11,
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  borderRadius: 5,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Exit Preview
              </button>
              <button
                onClick={() => {
                  const version = versions.find(v => v.version_number === previewingVersion);
                  if (version) setConfirmRestore(version.version_number);
                }}
                style={{
                  padding: '5px 12px',
                  fontSize: 11,
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: 5,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Restore This Version
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#111827' }}>
              Version History
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              {versions.length} version{versions.length !== 1 ? 's' : ''} saved
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 20,
              color: '#9ca3af',
              cursor: 'pointer',
              padding: 4,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
              Loading version history...
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#dc2626' }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>⚠️</div>
              {error}
            </div>
          ) : versions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>📋</div>
              No version history yet.
              <br />
              <span style={{ fontSize: 12 }}>Versions are saved automatically when you make changes.</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {versions.map((version, index) => {
                const isSelected = selectedVersion?.version_number === version.version_number;
                const isConfirming = confirmRestore === version.version_number;

                return (
                  <div
                    key={version.id}
                    style={{
                      border: `1px solid ${isSelected ? '#3b82f6' : '#e5e7eb'}`,
                      borderRadius: 8,
                      backgroundColor: isSelected ? '#eff6ff' : '#fff',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Version header */}
                    <div
                      style={{
                        padding: '12px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                      }}
                      onClick={() => handlePreviewVersion(version.version_number)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            backgroundColor: index === 0 ? '#dbeafe' : '#f3f4f6',
                            color: index === 0 ? '#2563eb' : '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          v{version.version_number}
                        </span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
                            {formatDate(version.created_at)}
                            {index === 0 && (
                              <span
                                style={{
                                  marginLeft: 8,
                                  fontSize: 10,
                                  backgroundColor: '#dbeafe',
                                  color: '#2563eb',
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                  fontWeight: 600,
                                }}
                              >
                                LATEST
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 11, color: '#9ca3af' }}>
                            by {version.created_by || 'unknown'}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {previewLoading && isSelected && (
                          <span style={{ fontSize: 11, color: '#6b7280' }}>Loading...</span>
                        )}
                        <span style={{ fontSize: 11, color: '#9ca3af' }}>
                          {isSelected ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>

                    {/* Expanded details */}
                    {isSelected && selectedVersion && (
                      <div
                        style={{
                          padding: '12px 14px',
                          borderTop: '1px solid #e5e7eb',
                          backgroundColor: '#f9fafb',
                        }}
                      >
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>
                          {formatFullDate(selectedVersion.created_at)}
                        </div>
                        
                        {/* Stats */}
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 8,
                            marginBottom: 12,
                          }}
                        >
                          {(() => {
                            const summary = getVersionSummary(selectedVersion.data);
                            return (
                              <>
                                <div style={{ fontSize: 12, color: '#374151' }}>
                                  <strong>{summary.equipments}</strong> Equipment
                                </div>
                                <div style={{ fontSize: 12, color: '#374151' }}>
                                  <strong>{summary.nodes}</strong> Lines
                                </div>
                                <div style={{ fontSize: 12, color: '#374151' }}>
                                  <strong>{summary.equipmentInstruments}</strong> Eq. Instruments
                                </div>
                                <div style={{ fontSize: 12, color: '#374151' }}>
                                  <strong>{summary.nodeInstruments}</strong> Line Instruments
                                </div>
                              </>
                            );
                          })()}
                        </div>

                        {/* Action buttons */}
                        {isConfirming ? (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 8,
                            padding: '10px 12px',
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: 6,
                            marginTop: 8,
                          }}>
                            <span style={{ fontSize: 12, color: '#991b1b', fontWeight: 500, flex: 1 }}>
                              ⚠️ This will overwrite current data. Are you sure?
                            </span>
                            <button
                              onClick={() => handleRestoreVersion(version.version_number)}
                              disabled={restoring}
                              style={{
                                padding: '6px 14px',
                                fontSize: 12,
                                backgroundColor: '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: 6,
                                cursor: restoring ? 'wait' : 'pointer',
                                fontWeight: 600,
                              }}
                            >
                              {restoring ? 'Restoring...' : 'Yes, Restore'}
                            </button>
                            <button
                              onClick={() => setConfirmRestore(null)}
                              disabled={restoring}
                              style={{
                                padding: '6px 14px',
                                fontSize: 12,
                                backgroundColor: 'white',
                                color: '#374151',
                                border: '1px solid #e5e7eb',
                                borderRadius: 6,
                                cursor: 'pointer',
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8 }}>
                            {/* View/Preview button */}
                            {onPreview && (
                              <button
                                onClick={() => {
                                  onPreview(selectedVersion.data, version.version_number);
                                  onClose();
                                }}
                                style={{
                                  padding: '6px 14px',
                                  fontSize: 12,
                                  backgroundColor: previewingVersion === version.version_number ? '#dbeafe' : '#f3f4f6',
                                  color: previewingVersion === version.version_number ? '#1d4ed8' : '#374151',
                                  border: `1px solid ${previewingVersion === version.version_number ? '#93c5fd' : '#e5e7eb'}`,
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  fontWeight: 500,
                                }}
                              >
                                {previewingVersion === version.version_number ? '👁️ Viewing' : '👁️ View'}
                              </button>
                            )}
                            {/* Restore button */}
                            <button
                              onClick={() => setConfirmRestore(version.version_number)}
                              style={{
                                padding: '6px 14px',
                                fontSize: 12,
                                backgroundColor: '#2563eb',
                                color: 'white',
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontWeight: 600,
                              }}
                            >
                              🔄 Restore
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            fontSize: 11,
            color: '#6b7280',
            textAlign: 'center',
          }}
        >
          Versions are automatically saved when you make changes. Up to 25 versions are kept.
        </div>
      </div>
    </div>
  );
};

export default VersionHistoryModal;
